import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  confirmEmailWithToken,
  sendEmailConfirmation,
  EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS,
} from '../../lib/emailConfirmation';
import { Button, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';

const fullPageSpinner = (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 gap-3">
    <Spinner size="lg" className="text-primary h-8 w-8" />
    <p className="text-sm">Confirming your email…</p>
  </div>
);

export default function ConfirmEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const { user, loading, refreshUser } = useAuth();

  const [phase, setPhase] = useState('loading'); // loading | success | error | missing
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const ranRef = useRef(false);

  useEffect(() => {
    if (!cooldownUntil) {
      setCooldownLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setCooldownLeft(left);
      if (left <= 0) setCooldownUntil(0);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  useEffect(() => {
    if (loading) return;
    if (!token) {
      setPhase('missing');
      setMessage('This confirmation link is missing a token.');
      return;
    }

    if (!user) {
      const redirectTarget = `/confirm-email?token=${encodeURIComponent(token)}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`, { replace: true });
      return;
    }

    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        const result = await confirmEmailWithToken(token);
        await refreshUser();
        setPhase('success');
        setMessage(
          result?.already_confirmed
            ? 'Your email was already confirmed.'
            : 'Your email has been confirmed.',
        );
        window.setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      } catch (err) {
        setPhase('error');
        setMessage(err.message || 'This confirmation link is invalid or has expired.');
      }
    };

    void run();
  }, [loading, user, token, navigate, refreshUser]);

  const handleResend = async () => {
    if (resending || cooldownLeft > 0) return;
    setResending(true);
    try {
      const result = await sendEmailConfirmation();
      if (result?.cooldown) {
        setMessage(result.message || 'Please wait a moment before requesting another confirmation email.');
      } else {
        setMessage('Check your inbox for a new confirmation link.');
      }
      setCooldownUntil(Date.now() + EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS);
    } catch (err) {
      setMessage(err.message || 'Could not resend confirmation email.');
    } finally {
      setResending(false);
    }
  };

  if (loading || phase === 'loading') {
    return fullPageSpinner;
  }

  if (!supabase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
          <p className="text-gray-700 text-sm">Email confirmation requires Supabase configuration.</p>
          <Link to="/login" className="mt-4 inline-block text-sm font-medium text-primary hover:text-primary-dark">
            Back to sign in
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200 text-center">
        <div className="mx-auto h-12 w-12 bg-primary rounded-lg shadow-sm flex items-center justify-center mb-4">
          <span className="text-white font-bold text-2xl">P</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {phase === 'success' ? 'Email confirmed' : 'Confirm your email'}
        </h2>
        <p className="text-sm text-gray-600 mb-6">{message}</p>

        {phase === 'success' ? (
          <p className="text-sm text-gray-500">Redirecting to your dashboard…</p>
        ) : (
          <div className="space-y-3">
            {user && (
              <Button
                type="button"
                className="w-full"
                isLoading={resending}
                disabled={cooldownLeft > 0}
                onClick={() => void handleResend()}
              >
                {cooldownLeft > 0
                  ? `Resend confirmation email (${cooldownLeft}s)`
                  : 'Resend confirmation email'}
              </Button>
            )}
            <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/dashboard')}>
              Go to dashboard
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
